import React, { useState, useEffect } from 'react';
import { ArrowLeft, Store, Plus, CheckCircle, XCircle, ExternalLink, Settings, Package, ShoppingBag, RefreshCw, ChevronRight, X, Image } from 'lucide-react';
import AddMarketplaceModal from './AddMarketplaceModal';

const ClientDashboard = ({ client, onBack, onSelectMarketplace }) => {
  const [clientData, setClientData] = useState(client);
  const [loading, setLoading] = useState(false);
  const [showAddMarketplace, setShowAddMarketplace] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [imageOptimization, setImageOptimization] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchClientData();
  }, [client.id]);

  useEffect(() => {
    const wooConnection = clientData.woocommerceConnections?.[0];
    if (wooConnection) {
      setImageOptimization(wooConnection.leroyImageOptimization || false);
    }
  }, [clientData]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://syncbridge.co.za/api'}/tenants/${client.id}`);
      const data = await response.json();
      setClientData(data);
    } catch (error) {
      console.error('Failed to fetch client:', error);
    } finally {
      setLoading(false);
    }
  };

  const testWooConnection = async () => {
    const wooConnection = clientData.woocommerceConnections?.[0];
    if (!wooConnection) return;

    try {
      const response = await fetch(`${window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://syncbridge.co.za/api'}/woocommerce/connections/${wooConnection.id}/test`, {
        method: 'POST',
      });
      const data = await response.json();
      alert(data.success ? 'Connection successful!' : 'Connection failed');
      fetchClientData();
    } catch (error) {
      alert('Connection test failed');
    }
  };

  const toggleImageOptimization = async () => {
    const wooConnection = clientData.woocommerceConnections?.[0];
    if (!wooConnection) return;

    setSavingSettings(true);
    try {
      const newValue = !imageOptimization;
      const response = await fetch(`${window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://syncbridge.co.za/api'}/woocommerce/connections/${wooConnection.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leroyImageOptimization: newValue }),
      });

      if (response.ok) {
        setImageOptimization(newValue);
        fetchClientData();
      }
    } catch (error) {
      console.error('Failed to update setting:', error);
    } finally {
      setSavingSettings(false);
    }
  };

  const getMarketplaceConfig = (type) => {
    const configs = {
      LEROY_MERLIN: {
        name: 'Leroy Merlin',
        color: '#78be20',
        bgColor: '#f0f9e8',
        description: 'Home improvement marketplace',
      },
    };
    return configs[type] || { name: type, color: '#6e6e73', bgColor: '#f5f5f7', description: 'Marketplace' };
  };

  const wooConnection = clientData.woocommerceConnections?.[0];
  const hasLeroyMerlin = clientData.marketplaceConnections?.some((mc) => mc.marketplaceType === 'LEROY_MERLIN');
  
  const availableMarketplaces = ['LEROY_MERLIN'].filter(
    (type) => !clientData.marketplaceConnections?.some((mc) => mc.marketplaceType === type)
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={onBack}>
          <ArrowLeft size={20} />
          All Clients
        </button>
      </div>

      <div style={styles.clientCard}>
        <div style={styles.clientHeader}>
          <div style={styles.clientLogo}>
            {clientData.logo ? (
              <img src={clientData.logo} alt={clientData.name} style={styles.logoImage} />
            ) : (
              <Store size={40} color="#8e8e93" />
            )}
          </div>
          <div style={styles.clientInfo}>
            <h1 style={styles.clientName}>{clientData.name}</h1>
            {clientData.website && (
              <a href={clientData.website} target="_blank" rel="noopener noreferrer" style={styles.clientWebsite}>
                {clientData.website.replace('https://', '').replace('http://', '')}
                <ExternalLink size={14} />
              </a>
            )}
          </div>
          <button style={styles.settingsButton} onClick={() => setShowSettings(true)}>
            <Settings size={20} />
          </button>
        </div>

        <div style={styles.statsRow}>
          <div style={styles.statBox}>
            <Package size={20} color="#007aff" />
            <div style={styles.statContent}>
              <span style={styles.statValue}>{clientData._count?.stagedProducts || 0}</span>
              <span style={styles.statLabel}>Products</span>
            </div>
          </div>
          <div style={styles.statBox}>
            <ShoppingBag size={20} color="#5856d6" />
            <div style={styles.statContent}>
              <span style={styles.statValue}>{clientData.marketplaceConnections?.length || 0}</span>
              <span style={styles.statLabel}>Marketplaces</span>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>WooCommerce Store</h2>
        {wooConnection ? (
          <div style={styles.wooCard}>
            <div style={styles.wooHeader}>
              <div style={styles.wooIcon}>
                <ShoppingBag size={24} color="#7c3aed" />
              </div>
              <div style={styles.wooInfo}>
                <h3 style={styles.wooName}>{wooConnection.storeUrl.replace('https://', '')}</h3>
                <div style={styles.wooStatus}>
                  {wooConnection.status === 'CONNECTED' ? (
                    <>
                      <CheckCircle size={14} color="#34c759" />
                      <span style={{ color: '#34c759' }}>Connected</span>
                    </>
                  ) : (
                    <>
                      <XCircle size={14} color="#ff3b30" />
                      <span style={{ color: '#ff3b30' }}>Disconnected</span>
                    </>
                  )}
                </div>
              </div>
              <button style={styles.testButton} onClick={testWooConnection}>
                <RefreshCw size={16} />
                Test
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.emptyWoo}>
            <p>No WooCommerce store connected</p>
          </div>
        )}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Marketplaces</h2>
        </div>

        <div style={styles.marketplacesGrid}>
          {/* Leroy Merlin (Mirakl) connections */}
          {clientData.marketplaceConnections?.map((mc) => {
            const config = getMarketplaceConfig(mc.marketplaceType);
            return (
              <div key={mc.id} style={styles.marketplaceCard} onClick={() => onSelectMarketplace(clientData, mc)}>
                <div style={{ ...styles.marketplaceIconBox, backgroundColor: config.bgColor }}>
                  <ShoppingBag size={24} color={config.color} />
                </div>
                <div style={styles.marketplaceInfo}>
                  <h3 style={styles.marketplaceName}>{config.name}</h3>
                  <p style={styles.marketplaceDesc}>{config.description}</p>
                  <div style={styles.marketplaceStatus}>
                    <span style={{ ...styles.statusDot, backgroundColor: mc.status === 'CONNECTED' ? '#34c759' : '#ff9500' }} />
                    <span style={styles.statusText}>{mc.status === 'CONNECTED' ? 'Connected' : 'Disconnected'}</span>
                    <span style={styles.shopId}>Shop: {mc.shopId}</span>
                  </div>
                </div>
                <ChevronRight size={20} color="#c7c7cc" />
              </div>
            );
          })}

          {/* Available marketplaces to connect */}
          {availableMarketplaces.map((type) => {
            const config = getMarketplaceConfig(type);
            return (
              <div
                key={type}
                style={styles.marketplaceCardInactive}
                onClick={() => setShowAddMarketplace(true)}
              >
                <div style={{ ...styles.marketplaceIconBox, backgroundColor: '#f5f5f7' }}>
                  <ShoppingBag size={24} color="#9ca3af" />
                </div>
                <div style={styles.marketplaceInfo}>
                  <h3 style={{ ...styles.marketplaceName, color: '#8e8e93' }}>{config.name}</h3>
                  <p style={styles.marketplaceDesc}>{config.description}</p>
                  <div style={styles.connectPrompt}>
                    <Plus size={14} color="#007aff" />
                    <span style={styles.connectText}>Connect</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showAddMarketplace && (
        <AddMarketplaceModal
          client={clientData}
          onClose={() => setShowAddMarketplace(false)}
          onSuccess={() => {
            setShowAddMarketplace(false);
            fetchClientData();
          }}
        />
      )}

      {showSettings && (
        <div style={styles.modalOverlay} onClick={() => setShowSettings(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Settings</h2>
              <button style={styles.closeBtn} onClick={() => setShowSettings(false)}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.settingSection}>
                <h3 style={styles.settingSectionTitle}>Leroy Merlin</h3>

                <div style={styles.settingRow}>
                  <div style={styles.settingInfo}>
                    <div style={styles.settingIcon}>
                      <Image size={20} color="#007aff" />
                    </div>
                    <div>
                      <p style={styles.settingLabel}>Image Optimization (600x600)</p>
                      <p style={styles.settingDesc}>
                        Use optimized 600x600 images for Leroy Merlin.
                        Requires WordPress snippet installed.
                      </p>
                    </div>
                  </div>
                  <button
                    style={{
                      ...styles.toggle,
                      backgroundColor: imageOptimization ? '#34c759' : '#e5e5e5',
                    }}
                    onClick={toggleImageOptimization}
                    disabled={savingSettings}
                  >
                    <span
                      style={{
                        ...styles.toggleKnob,
                        transform: imageOptimization ? 'translateX(20px)' : 'translateX(0)',
                      }}
                    />
                  </button>
                </div>

                {!imageOptimization && (
                  <div style={styles.snippetBox}>
                    <p style={styles.snippetTitle}>WordPress Snippet Required</p>
                    <p style={styles.snippetDesc}>
                      Add this to your WordPress site via Code Snippets plugin:
                    </p>
                    <pre style={styles.codeBlock}>
{`// Add 600x600 image size for Leroy Merlin
add_image_size('leroy-merlin', 600, 600, false);`}
                    </pre>
                    <p style={styles.snippetNote}>
                      After adding, run "Regenerate Thumbnails" plugin on all images.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '24px 40px',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '24px',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#f5f5f7',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#1d1d1f',
    cursor: 'pointer',
  },
  clientCard: {
    backgroundColor: '#fff',
    borderRadius: '20px',
    padding: '28px',
    marginBottom: '24px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
  },
  clientHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '24px',
  },
  clientLogo: {
    width: '80px',
    height: '80px',
    borderRadius: '20px',
    backgroundColor: '#f5f5f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  clientInfo: {
    flex: 1,
  },
  clientName: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1d1d1f',
    margin: '0 0 6px 0',
  },
  clientWebsite: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '15px',
    color: '#007aff',
    textDecoration: 'none',
  },
  settingsButton: {
    padding: '12px',
    backgroundColor: '#f5f5f7',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    color: '#6e6e73',
  },
  statsRow: {
    display: 'flex',
    gap: '16px',
  },
  statBox: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    backgroundColor: '#f5f5f7',
    borderRadius: '14px',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1d1d1f',
  },
  statLabel: {
    fontSize: '13px',
    color: '#6e6e73',
  },
  section: {
    marginBottom: '32px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1d1d1f',
    margin: '0 0 16px 0',
  },
  wooCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
  },
  wooHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  wooIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '14px',
    backgroundColor: '#f3e8ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  wooInfo: {
    flex: 1,
  },
  wooName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1d1d1f',
    margin: '0 0 4px 0',
  },
  wooStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
  },
  testButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: '#f5f5f7',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#1d1d1f',
    cursor: 'pointer',
  },
  emptyWoo: {
    padding: '40px',
    textAlign: 'center',
    color: '#8e8e93',
    backgroundColor: '#fff',
    borderRadius: '16px',
  },
  marketplacesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
  },
  marketplaceCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
    border: '1px solid transparent',
  },
  marketplaceCardInactive: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    cursor: 'pointer',
    border: '2px dashed #d1d1d6',
  },
  marketplaceIconBox: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  marketplaceInfo: {
    flex: 1,
    minWidth: 0,
  },
  marketplaceName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1d1d1f',
    margin: '0 0 4px 0',
  },
  marketplaceDesc: {
    fontSize: '13px',
    color: '#8e8e93',
    margin: '0 0 8px 0',
  },
  marketplaceStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  statusText: {
    color: '#6e6e73',
  },
  shopId: {
    color: '#8e8e93',
    marginLeft: '8px',
    paddingLeft: '8px',
    borderLeft: '1px solid #e5e5e5',
  },
  connectPrompt: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  connectText: {
    fontSize: '13px',
    color: '#007aff',
    fontWeight: '500',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
  },
  modalBody: {
    padding: '24px',
  },
  settingSection: {
    marginBottom: '24px',
  },
  settingSectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '16px',
  },
  settingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    marginBottom: '12px',
  },
  settingInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flex: 1,
  },
  settingIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: '#eff6ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  settingLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 4px 0',
  },
  settingDesc: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0,
    lineHeight: '1.4',
  },
  toggle: {
    width: '50px',
    height: '30px',
    borderRadius: '15px',
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background-color 0.2s',
    flexShrink: 0,
    marginLeft: '12px',
  },
  toggleKnob: {
    position: 'absolute',
    top: '3px',
    left: '3px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    transition: 'transform 0.2s',
  },
  snippetBox: {
    backgroundColor: '#fefce8',
    border: '1px solid #fef08a',
    borderRadius: '12px',
    padding: '16px',
  },
  snippetTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#854d0e',
    margin: '0 0 8px 0',
  },
  snippetDesc: {
    fontSize: '13px',
    color: '#a16207',
    margin: '0 0 12px 0',
  },
  codeBlock: {
    backgroundColor: '#422006',
    color: '#fef3c7',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontFamily: 'monospace',
    overflow: 'auto',
    margin: '0 0 12px 0',
    whiteSpace: 'pre-wrap',
  },
  snippetNote: {
    fontSize: '12px',
    color: '#a16207',
    margin: 0,
    fontStyle: 'italic',
  },
};

export default ClientDashboard;
